import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { triggerInAppReview } from '../utils/inAppReview';
import { runPurchaseWithRetry } from '../utils/purchaseRetry';
import { track } from '../utils/analytics';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  ArrowLeft, Send, Loader2, MessageSquare,
  Flame, Volume2, VolumeX, Mic, MicOff, User, Users, Sparkles,
  Settings, Music, Globe, MessageCircle, HelpCircle, Mail, Crown, Lock, ImageIcon,
  Star, ThumbsUp, ThumbsDown, Download, X, Flag, AlertTriangle, Bell, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { usePurchase } from '../hooks/usePurchase';
import { usePlayPrices } from '../hooks/usePlayPrices';
import PremiumModal from '../components/PremiumModal';
import SmartImage from '../components/SmartImage';
import ActivationLoader from '../components/ActivationLoader';
import PurchaseCelebrationModal from '../components/PurchaseCelebrationModal';
import PremiumBadge from '../components/PremiumBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dialogues required for each level
const LEVEL_DIALOGUES = {
  2: 25,
  3: 60,
  4: 140,
  5: 220
};

const LEVEL_NAMES = {
  fr: {
    1: 'Découverte',
    2: 'Connaissance',
    3: 'Amitié',
    4: 'Rapprochement',
    5: 'Romance'
  },
  en: {
    1: 'Discovery',
    2: 'Acquaintance',
    3: 'Friendship',
    4: 'Getting Closer',
    5: 'Romance'
  },
  es: {
    1: 'Descubrimiento',
    2: 'Conocimiento',
    3: 'Amistad',
    4: 'Acercamiento',
    5: 'Romance'
  },
  de: {
    1: 'Entdeckung',
    2: 'Bekanntschaft',
    3: 'Freundschaft',
    4: 'Annäherung',
    5: 'Romance'
  },
  pt: {
    1: 'Descoberta',
    2: 'Conhecimento',
    3: 'Amizade',
    4: 'Aproximação',
    5: 'Romance'
  }
};

export default function ChatPage() {
  const navigate = useNavigate();
  const { gender, characterId, voiceId, level } = useParams();
  const { 
    user, token, logout, refreshUser,
    isCharacterUnlocked, isLevelUnlockedForCharacter, 
    getCharacterDialogues, getCharacterLevels,
    updateCharacterProgress,
    isSubscribed, subscriptionExpiry,
  } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { startPurchase: _basePurchase } = usePurchase();
  // Wrap with the alreadyOwned-aware retry helper so every existing
  // startPurchase(...) call site benefits from the "Réessayer" toast UX
  // when Google Play reports a stuck previous purchase.
  const startPurchase = React.useCallback(
    (params) => runPurchaseWithRetry({ startPurchase: _basePurchase, params, toast, language }),
    [_basePurchase, language]
  );
  const playPrices = usePlayPrices();
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [character, setCharacter] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(false);  // Default to text mode
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [newLevel, setNewLevel] = useState(null);
  const [localDialogues, setLocalDialogues] = useState(0);
  // Triggers a short pulse/glow on the counter + progress bar every time the
  // dialogue count increments. Resets automatically after the animation runs.
  const [pulseDialogues, setPulseDialogues] = useState(false);
  const prevDialoguesRef = useRef(0);
  useEffect(() => {
    if (localDialogues > prevDialoguesRef.current) {
      setPulseDialogues(true);
      const t = setTimeout(() => setPulseDialogues(false), 700);
      prevDialoguesRef.current = localDialogues;
      return () => clearTimeout(t);
    }
    prevDialoguesRef.current = localDialogues;
  }, [localDialogues]);
  const [localUnlockedLevels, setLocalUnlockedLevels] = useState([1]);
  const [sessionMessageCount, setSessionMessageCount] = useState(0);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [purchasingLevel, setPurchasingLevel] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  // True between the moment the Google Play popup closes successfully and the
  // celebration modal appearing. Drives a full-screen "Activation en cours…"
  // loader so the user doesn't see a frozen UI while the backend verifies.
  const [activating, setActivating] = useState(false);
  // L5 (Romance) message-block paywall — populated when backend returns 402.
  const [l5Paywall, setL5Paywall] = useState(null);
  const [purchasingL5Block, setPurchasingL5Block] = useState(false);
  // Celebration modal after a successful Play/Stripe purchase.
  // type: 'level' | 'photos' | 'subscription'  /  data: { level?, characterId? }
  const [celebration, setCelebration] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [listenDuration, setListenDuration] = useState(0);
  const listenTimerRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [musicEnabled, setMusicEnabled] = useState(() => localStorage.getItem('velvet_music_muted') !== 'true');
  const [responseLength, setResponseLength] = useState(() => localStorage.getItem('velvet_response_length') || 'medium');
  const [photosUnlocked, setPhotosUnlocked] = useState(false);
  const [purchasingPhotos, setPurchasingPhotos] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewStep, setReviewStep] = useState(1);
  const [reviewLiked, setReviewLiked] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewDone, setReviewDone] = useState(() => localStorage.getItem('velvet_review_done') === 'true');
  const [reviewDismissed, setReviewDismissed] = useState(false);
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  const [notifGranted, setNotifGranted] = useState(() => localStorage.getItem('erotika_notif_granted') === 'true');
  const [notifDenied, setNotifDenied] = useState(() => localStorage.getItem('erotika_notif_denied') === 'true');
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // {type: 'message'|'photo'|'conversation', index, content, photoUrl}
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  // Promo code state (testers / Google Play closed-testing)
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [promoMessage, setPromoMessage] = useState(null); // { type: 'success' | 'error', text }

  const REPORT_REASONS = {
    fr: [
      { value: 'non_consenting_sexual', label: 'Contenu sexuel non consenti' },
      { value: 'violent_degrading', label: 'Propos violents ou dégradants' },
      { value: 'minor_reference', label: 'Référence à des mineurs' },
      { value: 'discriminatory', label: 'Contenu discriminatoire' },
      { value: 'other', label: 'Autre' }
    ],
    en: [
      { value: 'non_consenting_sexual', label: 'Non-consenting sexual content' },
      { value: 'violent_degrading', label: 'Violent or degrading language' },
      { value: 'minor_reference', label: 'Reference to minors' },
      { value: 'discriminatory', label: 'Discriminatory content' },
      { value: 'other', label: 'Other' }
    ]
  };

  const handleReport = (target) => {
    setReportTarget(target);
    setReportReason('');
    setReportDescription('');
    setReportDone(false);
    setReportOpen(true);
  };

  const submitReport = async () => {
    if (!reportReason) return;
    if (reportReason === 'other' && !reportDescription.trim()) return;
    setReportSubmitting(true);
    try {
      await axios.post(`${API}/reports`, {
        reason: reportReason,
        description: reportDescription || null,
        character_id: characterId,
        conversation_id: conversationId,
        message_index: reportTarget?.index ?? null,
        message_content: reportTarget?.content ?? null,
        photo_url: reportTarget?.photoUrl ?? null
      }, { headers: { Authorization: `Bearer ${token}` } });
      setReportDone(true);
    } catch (err) {
      console.error('Report error:', err);
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleRedeemPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoSubmitting(true);
    setPromoMessage(null);
    try {
      const resp = await axios.post(
        `${API}/promo/redeem`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const msg = resp.data?.message || ({
        fr: 'Code accepté ! Tout est débloqué',
        en: 'Code accepted! Everything unlocked',
        es: '¡Código aceptado! Todo desbloqueado',
        de: 'Code akzeptiert! Alles freigeschaltet',
        pt: 'Código aceito! Tudo desbloqueado',
      }[language] || 'Code accepted!');
      setPromoMessage({ type: 'success', text: msg });
      toast.success(msg);
      // Refresh user state so unlocked content reflects immediately
      await refreshUser();
      // Close modal after a short success display + reload to refresh chat & nav
      setTimeout(() => {
        setPromoOpen(false);
        window.location.reload();
      }, 1500);
    } catch (err) {
      const errMsg = err.response?.data?.detail || ({
        fr: 'Code invalide.',
        en: 'Invalid code.',
        es: 'Código no válido.',
        de: 'Ungültiger Code.',
        pt: 'Código inválido.',
      }[language] || 'Invalid code.');
      setPromoMessage({ type: 'error', text: errMsg });
    } finally {
      setPromoSubmitting(false);
    }
  };
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const previousUnlockedLevelsRef = useRef([1]);
  const recognitionRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Fix mobile keyboard covering input area
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.style.height = `${vv.height}px`;
      }
    };

    handleResize();
    vv.addEventListener('resize', handleResize);
    vv.addEventListener('scroll', handleResize);

    return () => {
      vv.removeEventListener('resize', handleResize);
      vv.removeEventListener('scroll', handleResize);
    };
  }, []);

  // Scroll input into view when focused (mobile keyboard)
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleFocus = () => {
      setTimeout(() => {
        input.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300);
    };

    input.addEventListener('focus', handleFocus);
    return () => input.removeEventListener('focus', handleFocus);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    // Only auto-scroll when new messages are added, not on initial load
    if (messages.length > prevMessageCountRef.current && prevMessageCountRef.current > 0) {
      scrollToBottom();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  // Check if user just returned from a purchase — trigger review
  useEffect(() => {
    if (reviewDone || reviewDismissed) return;
    const purchaseFlag = localStorage.getItem('erotika_review_after_purchase');
    if (purchaseFlag) {
      localStorage.removeItem('erotika_review_after_purchase');
      setTimeout(() => triggerInAppReview(() => setShowReview(true)), 3000);
    }
  }, []);

  // Initialize Speech Recognition
  const speechTextRef = useRef('');
  const isListeningRef = useRef(false);

  // Heuristic: Web Speech API doesn't insert punctuation. If a transcript chunk
  // looks like a question (interrogative words/inversion), append "?" so the
  // user doesn't have to edit it manually before sending. For longer affirmative
  // chunks (>=5 words) we append "." so the message reads cleanly.
  const addPunctuationIfNeeded = (text, lang) => {
    if (!text) return text;
    const trimmed = text.trim();
    if (!trimmed) return text;
    // Already has terminal punctuation
    if (/[?!.…,;:]$/.test(trimmed)) return text;

    const lower = trimmed.toLowerCase();

    const patterns = {
      fr: [
        /^(qui|que|quoi|quand|o[uù]|comment|pourquoi|combien|quel(le)?s?)\b/,
        /^est[- ]ce\b/,
        /\bqu['\u2019\s]est[- ]ce\b/,
        /^(es|est|as|a|ai|fais|fait|font|veux|veut|peux|peut|sais|sait|aimes|aime|vas|va|y a[- ]t[- ]il|serais|seras)\b/,
        /\b(as[- ]tu|es[- ]tu|fais[- ]tu|veux[- ]tu|peux[- ]tu|sais[- ]tu|aimes[- ]tu|vas[- ]tu|serais[- ]tu|aurais[- ]tu)\b/,
        /\ba[- ]t[- ]il\b/,
        /^tu\s+(as|es|veux|peux|sais|aimes|vas|fais|prends)\b/,
      ],
      en: [
        /^(what|who|whom|whose|where|when|why|how|which)\b/,
        /^(is|are|am|was|were|do|does|did|can|could|will|would|should|shall|may|might|have|has|had)\b/,
        /\b(don['\u2019]?t you|aren['\u2019]?t you|haven['\u2019]?t you|isn['\u2019]?t it|right)\s*$/,
      ],
      es: [
        /^(qu[eé]|qui[eé]n|cu[aá]ndo|d[oó]nde|c[oó]mo|por\s*qu[eé]|cu[aá]l|cu[aá]nto)\b/,
        /^(est[aá]s?|eres|eran|puedes|puede|quieres|quiere|sabes|sabe|tienes|tiene|vas|va|hay)\b/,
      ],
      de: [
        /^(was|wer|wen|wem|wessen|wann|wo|wohin|woher|wie|warum|weshalb|wieso|welche[rns]?)\b/,
        /^(ist|sind|bin|war|waren|hast|hat|habe|kannst|kann|willst|will|wirst|wird|soll|sollst|darfst|darf|m[oö]chtest|m[oö]chte)\b/,
      ],
      pt: [
        /^(o\s+que|que|quem|quando|onde|como|por\s*que|porqu[eê]|qual|quais|quanto|quantos)\b/,
        /^(est[aá]s?|[eé]s|[eé]|s[aã]o|foi|foram|tens|tem|podes|pode|queres|quer|sabes|sabe|vais|vai|h[aá])\b/,
      ],
    };

    const list = patterns[lang] || patterns.en;
    const isQuestion = list.some((re) => re.test(lower));
    if (isQuestion) {
      return text.replace(/\s*$/, '? ');
    }

    // Affirmative: add "." only for longer chunks to avoid punctuating short
    // utterances like "oui", "ok", "merci" that the user may want to continue.
    const wordCount = trimmed.split(/\s+/).length;
    if (wordCount >= 5) {
      return text.replace(/\s*$/, '. ');
    }

    return text;
  };

  // Capitalize first letter of the whole text AND first letter after a sentence
  // terminator (., !, ?). Useful because many browsers return voice transcripts
  // fully in lowercase.
  const capitalizeSentences = (text) => {
    if (!text) return text;
    // First non-whitespace letter of the whole string
    let out = text.replace(/^(\s*)(\p{Ll})/u, (_m, pre, ch) => pre + ch.toUpperCase());
    // Letter following a sentence terminator + whitespace
    out = out.replace(/([.!?…]\s+)(\p{Ll})/gu, (_m, sep, ch) => sep + ch.toUpperCase());
    // Standalone "i" in English → "I"
    out = out.replace(/\bi\b/g, 'I');
    return out;
  };

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.lang = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', de: 'de-DE', pt: 'pt-PT' }[language] || 'en-US';
      
      recognition.onresult = (event) => {
        // Only process NEW final results using resultIndex
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const raw = event.results[i][0].transcript;
          const punctuated = addPunctuationIfNeeded(raw, language);
          // Ensure a space between previous chunk and new one
          const prev = speechTextRef.current;
          const joiner = prev && !/\s$/.test(prev) ? ' ' : '';
          speechTextRef.current = prev + joiner + punctuated;
        }
        speechTextRef.current = capitalizeSentences(speechTextRef.current);
        setInput(speechTextRef.current);
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed') {
          isListeningRef.current = false;
          setIsListening(false);
          toast.error(language === 'fr' 
            ? 'Microphone non autorisé. Veuillez autoriser l\'accès.' 
            : 'Microphone not allowed. Please grant access.');
        }
      };
      
      recognition.onend = () => {
        isListeningRef.current = false;
        setIsListening(false);
        if (listenTimerRef.current) { clearInterval(listenTimerRef.current); listenTimerRef.current = null; }
      };
      
      recognitionRef.current = recognition;
    }
    
    return () => {
      isListeningRef.current = false;
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (listenTimerRef.current) { clearInterval(listenTimerRef.current); }
    };
  }, [language]);

  // Toggle speech recognition
  const toggleListening = () => {
    if (!speechSupported) {
      toast.error(language === 'fr' 
        ? 'La reconnaissance vocale n\'est pas supportée par votre navigateur' 
        : 'Speech recognition is not supported by your browser');
      return;
    }
    
    if (isListening) {
      isListeningRef.current = false;
      recognitionRef.current?.stop();
      setIsListening(false);
      if (listenTimerRef.current) { clearInterval(listenTimerRef.current); listenTimerRef.current = null; }
      setListenDuration(0);
    } else {
      // Stop any playing voice first
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        setIsPlayingVoice(false);
      }
      
      try {
        speechTextRef.current = input;
        isListeningRef.current = true;
        recognitionRef.current?.start();
        setIsListening(true);
        setListenDuration(0);
        listenTimerRef.current = setInterval(() => {
          setListenDuration(prev => prev + 1);
        }, 1000);
      } catch (error) {
        console.error('Speech recognition start error:', error);
      }
    }
  };

  // Initialize local state from auth context
  useEffect(() => {
    const dialogues = getCharacterDialogues(characterId);
    const levels = getCharacterLevels(characterId);
    setLocalDialogues(dialogues);
    setLocalUnlockedLevels(levels);
    previousUnlockedLevelsRef.current = levels;
  }, [characterId, getCharacterDialogues, getCharacterLevels]);

  useEffect(() => {
    fetchCharacter();
  }, []);

  // Check access
  useEffect(() => {
    if (!isLevelUnlockedForCharacter(characterId, parseInt(level))) {
      navigate(`/select-level/${gender}/${characterId}`);
    }
    if (!isCharacterUnlocked(characterId)) {
      navigate(`/select-character/${gender}`);
    }
  }, [level, characterId, isLevelUnlockedForCharacter, isCharacterUnlocked, navigate, gender, voiceId]);

  // Load existing conversation for this character on mount
  useEffect(() => {
    const loadExistingConversation = async () => {
      try {
        const response = await axios.get(`${API}/chat/character/${characterId}?language=${language}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.conversation) {
          setConversationId(response.data.conversation.id);
          const msgs = response.data.conversation.messages || [];
          setMessages(msgs);
          prevMessageCountRef.current = msgs.length;
          // Check if any photo is locked
          const hasLockedPhoto = msgs.some(m => m.photo_locked);
          setPhotosUnlocked(!hasLockedPhoto && msgs.some(m => m.image_url || m.video_url));
          // Scroll to bottom after initial load
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'instant' }), 100);
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    loadExistingConversation();
  }, [characterId, token]);

  const fetchCharacter = async () => {
    try {
      const response = await axios.get(`${API}/characters/${gender}?language=${language}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const char = response.data.find(c => c.id === characterId);
      setCharacter(char);
    } catch (error) {
      console.error('Error fetching character:', error);
    }
  };

  // Generate and play voice for AI response
  const playVoice = async (text) => {
    if (!voiceEnabled || !text) return;
    
    // Stop any currently playing audio
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current = null;
    }
    
    try {
      setIsPlayingVoice(true);
      
      const response = await axios.post(`${API}/tts/generate`, {
        text: text,
        character_id: characterId,
        language: language
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.audio_url) {
        const audio = new Audio(response.data.audio_url);
        voiceAudioRef.current = audio;
        
        audio.onended = () => {
          setIsPlayingVoice(false);
          voiceAudioRef.current = null;
        };
        
        audio.onerror = () => {
          setIsPlayingVoice(false);
          voiceAudioRef.current = null;
        };
        
        await audio.play();
      }
    } catch (error) {
      console.error('Voice generation error:', error);
      setIsPlayingVoice(false);
    }
  };

  // Stop voice playback
  const stopVoice = () => {
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current = null;
    }
    setIsPlayingVoice(false);
  };

  // Toggle voice and stop if currently playing
  const toggleVoice = () => {
    if (voiceEnabled && isPlayingVoice) {
      stopVoice();
    }
    setVoiceEnabled(!voiceEnabled);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const messageContent = input.trim();
    // First user message in this conversation → conversation_started.
    // We check for the absence of any prior `role: 'user'` message rather
    // than `messages.length === 0` because the conversation now opens with
    // an auto-greeting from the character (assistant role, length === 1).
    // This ensures the event fires exactly once, on the user's first reply.
    if (!messages.some(m => m.role === 'user')) {
      track('conversation_started', { character_id: characterId, character_name: character?.name, level: parseInt(level, 10) });
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversation_started', {
          character_id: characterId,
          character_name: character?.name,
          level: parseInt(level, 10)
        });
      }
    }
    const userMessage = {
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    speechTextRef.current = '';
    setLoading(true);

    // Stop voice recording if active
    if (isListeningRef.current) {
      isListeningRef.current = false;
      recognitionRef.current?.stop();
      setIsListening(false);
      if (listenTimerRef.current) { clearInterval(listenTimerRef.current); listenTimerRef.current = null; }
      setListenDuration(0);
    }

    try {
      const userTimezone = (() => {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; }
        catch (_) { return null; }
      })();
      const response = await axios.post(`${API}/chat/send`, {
        message: userMessage.content,
        conversation_id: conversationId,
        gender,
        character_id: characterId,
        voice_id: voiceId,
        eroticism_level: parseInt(level),
        language: language,
        response_length: responseLength,
        timezone: userTimezone,
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 45000
      });

      setConversationId(response.data.conversation_id);
      
      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      
      // Check if backend returned a new photo message (always last in the array)
      const backendMessages = response.data.messages || [];
      const lastBackendMsg = backendMessages[backendMessages.length - 1];
      const newPhotoMsg = (lastBackendMsg && (lastBackendMsg.image_url || lastBackendMsg.video_url)) ? lastBackendMsg : null;
      
      if (newPhotoMsg) {
        // Une photo a été envoyée : le texte est déjà dans la caption de la photo.
        // On affiche UNIQUEMENT la photo pour éviter une bulle de texte en double.
        setMessages(prev => [...prev, newPhotoMsg]);
      } else {
        setMessages(prev => [...prev, aiMessage]);
      }
      
      // Trigger review after every 20th user message (if not already reviewed or dismissed)
      if (!reviewDone && !reviewDismissed) {
        const totalUserMsgs = messages.filter(m => m.role === 'user').length + 1; // +1 for the message just sent
        if (totalUserMsgs > 0 && totalUserMsgs % 20 === 0) {
          setTimeout(() => triggerInAppReview(() => setShowReview(true)), 2000);
        }
      }
      
      // Trigger notification permission prompt at 15th msg, then every 25 msgs
      if (!notifGranted && !notifDenied && 'Notification' in window && Notification.permission !== 'granted') {
        const totalUserMsgs = messages.filter(m => m.role === 'user').length + 1;
        if (totalUserMsgs === 15 || (totalUserMsgs > 15 && (totalUserMsgs - 15) % 25 === 0)) {
          setTimeout(() => setShowNotifPrompt(true), 3000);
        }
      }
      
      // Play voice for AI response if enabled
      if (voiceEnabled) {
        playVoice(response.data.response);
      }
      
      // Increment session message count for upgrade prompt
      const newSessionCount = sessionMessageCount + 1;
      setSessionMessageCount(newSessionCount);
      
      // Update progress for this character
      if (response.data.character_progress) {
        const progress = response.data.character_progress;
        setLocalDialogues(progress.dialogues);
        setLocalUnlockedLevels(progress.unlocked_levels);
        
        // Update auth context
        updateCharacterProgress(characterId, progress);
        
        // Check for new level unlock
        if (response.data.new_level_unlocked) {
          setNewLevel(response.data.new_level_unlocked);
          setShowLevelUp(true);
        } else {
          // Check if user reached threshold for a paid level (4 or 5)
          const eligible = progress.eligible_levels || [];
          if (eligible.length > 0) {
            // Threshold reached but payment needed
            setShowUpgradePrompt(true);
          } else {
            // Show upgrade prompt every 15 messages if there are locked levels
            const maxUnlockedLevel = Math.max(...(progress.unlocked_levels || [1]));
            if (newSessionCount % 15 === 0 && maxUnlockedLevel < 5) {
              setShowUpgradePrompt(true);
            }
          }
        }
      }
    } catch (error) {
      console.error('Send message error:', error);
      // Message quota paywall — backend returned 402.
      // Two flavors share the same UX: `l5_block_exhausted` (non-subscriber at L5)
      // and `subscriber_quota_exhausted` (subscriber monthly quota).
      const detail = error.response?.data?.detail;
      const isQuotaExhausted = error.response?.status === 402 && detail && typeof detail === 'object'
        && (detail.code === 'l5_block_exhausted' || detail.code === 'subscriber_quota_exhausted');
      if (isQuotaExhausted) {
        track('paywall_seen', {
          reason: detail.code,
          character_id: detail.character_id,
          level: parseInt(level, 10),
          monthly_quota: detail.monthly_quota,
        });
        setL5Paywall({
          characterId: detail.character_id,
          blockSize: detail.block_size,
          paidBlocks: detail.paid_blocks,
          isSubscriberQuota: detail.code === 'subscriber_quota_exhausted',
          monthlyQuota: detail.monthly_quota,
        });
        setMessages(prev => prev.slice(0, -1));
        setInput(messageContent); // restore the typed message so user doesn't lose it
        return;
      }
      if (error.response?.status === 403) {
        toast.error(language === 'fr' ? 'Accès non autorisé' : 'Access not authorized');
        navigate(`/select-level/${gender}/${characterId}`);
      } else {
        toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : t('error'));
        setInput(messageContent);
      }
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  // Cleanup voice audio on unmount
  useEffect(() => {
    return () => {
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
      }
    };
  }, []);

  const characterName = character?.name || characterId;

  // Handle quick level purchase from prompt
  const handleQuickLevelPurchase = async () => {
    const maxUnlockedLevel = Math.max(...(localUnlockedLevels || [1]));
    const nextLevel = maxUnlockedLevel + 1;
    
    if (nextLevel > 5) return;
    
    setPurchasingLevel(true);
    try {
      const returnPath = `/chat/${gender}/${characterId}/${nextLevel}`;
      const res = await startPurchase({
        itemType: 'level',
        itemId: `${characterId}:${nextLevel}`,
        returnTo: returnPath,
        onPopupClosed: () => {
          // Google Play popup confirmed by the user — close any prompts and
          // show the full-screen activation loader during backend verify.
          setShowUpgradePrompt(false);
          setActivating(true);
        },
      });
      if (res.redirected) return;
      if (res.success) {
        track('one_time_purchase_completed', { type: 'level', character_id: characterId });
        setCelebration({ type: 'level', level: nextLevel });
      } else if (!res.cancelled) {
        toast.error(res.message || (language === 'fr' ? "Erreur lors de l'achat" : 'Purchase error'));
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'achat');
    } finally {
      setPurchasingLevel(false);
      setShowUpgradePrompt(false);
      setActivating(false);
    }
  };

  // Get the next level name for the prompt
  const getNextLevelName = () => {
    const maxUnlockedLevel = Math.max(...(localUnlockedLevels || [1]));
    const nextLevel = maxUnlockedLevel + 1;
    return nextLevel <= 5 ? (LEVEL_NAMES[language] || LEVEL_NAMES.fr)[nextLevel] : null;
  };

  // Calculate next level progress for this character
  const getNextLevelInfo = () => {
    const unlockedLevels = localUnlockedLevels || [1];
    const currentMaxUnlocked = Math.max(...unlockedLevels);
    const nextLevel = currentMaxUnlocked + 1;
    
    if (nextLevel > 5) {
      return { nextLevel: null, required: 0, progress: 100 };
    }
    
    const required = LEVEL_DIALOGUES[nextLevel] || 0;
    const progress = required > 0 ? Math.min(100, (localDialogues / required) * 100) : 100;
    
    return { nextLevel, required, progress };
  };

  const nextLevelInfo = getNextLevelInfo();

  const handleUnlockPhotos = async () => {
    setPurchasingPhotos(true);
    try {
      const returnPath = `/chat/${gender}/${characterId}/${level}`;
      const res = await startPurchase({
        itemType: 'photos',
        itemId: characterId,
        returnTo: returnPath,
        onPopupClosed: () => setActivating(true),
      });
      if (res.redirected) return;
      if (res.success) {
        track('one_time_purchase_completed', { type: 'photos', character_id: characterId });
        setCelebration({ type: 'photos' });
      } else if (!res.cancelled) {
        toast.error(res.message || 'Erreur');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setPurchasingPhotos(false);
      setActivating(false);
    }
  };

  const handleSubmitReview = async () => {
    setReviewSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        liked: reviewLiked,
        rating: reviewLiked ? reviewRating : null,
        comment: reviewComment || null,
        character_id: characterId
      }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem('velvet_review_done', 'true');
      setReviewDone(true);
      setShowReview(false);
      toast.success(language === 'fr' ? 'Merci pour votre avis !' : language === 'es' ? '¡Gracias por tu opinión!' : language === 'de' ? 'Danke für Ihre Bewertung!' : language === 'pt' ? 'Obrigado pela sua opinião!' : 'Thank you for your review!');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Push notification subscription handler
  const handleEnableNotifications = async () => {
    try {
      if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        toast.error(language === 'fr'
          ? "Ton navigateur ne supporte pas les notifications push."
          : "Your browser doesn't support push notifications.");
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        localStorage.setItem('erotika_notif_denied', 'true');
        setNotifDenied(true);
        setShowNotifPrompt(false);
        return;
      }

      const registration = await navigator.serviceWorker.register('/sw.js');
      // Force the browser to fetch the latest sw.js so users pick up icon/badge
      // changes without having to clear cache manually.
      try { await registration.update(); } catch (_e) { /* ignore */ }
      await navigator.serviceWorker.ready;

      const vapidRes = await axios.get(`${API}/notifications/vapid-key`);
      const vapidKey = vapidRes.data?.public_key;
      if (!vapidKey) {
        toast.error(language === 'fr'
          ? "Clés de notification non configurées côté serveur. Contacte l'équipe."
          : "Server notification keys not configured. Contact support.");
        return;
      }

      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
      };

      // If we already have an active subscription with a DIFFERENT application key,
      // we must unsubscribe first otherwise pushManager.subscribe() will fail silently.
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch (_) { /* ignore */ }
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      await axios.post(`${API}/notifications/subscribe`, {
        subscription: subscription.toJSON(),
        character_id: characterId,
        gender: gender,
        level: parseInt(level),
      }, { headers: { Authorization: `Bearer ${token}` } });

      localStorage.setItem('erotika_notif_granted', 'true');
      localStorage.removeItem('erotika_notif_denied');
      setNotifGranted(true);
      setShowNotifPrompt(false);
      toast.success(language === 'fr' ? 'Notifications activées !' : language === 'es' ? '¡Notificaciones activadas!' : language === 'de' ? 'Benachrichtigungen aktiviert!' : language === 'pt' ? 'Notificações ativadas!' : 'Notifications enabled!');
    } catch (err) {
      console.error('Notification subscription error:', err);
      toast.error(language === 'fr'
        ? `Erreur : ${err.message}`
        : `Error: ${err.message}`);
      setShowNotifPrompt(false);
    }
  };

  return (
    <>
    <div ref={chatContainerRef} className="bg-velvet-bg flex flex-col overflow-hidden" style={{ height: '100dvh' }}>
      {/* Level Up Modal */}
      {showLevelUp && newLevel && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="glass rounded-2xl p-8 max-w-md text-center animate-slideUp">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-main to-fuchsia-main flex items-center justify-center">
              <Sparkles className="text-white" size={40} />
            </div>
            <h2 className="font-heading text-3xl gradient-text mb-4">
              {language === 'fr' ? 'Félicitations !' : 'Congratulations!'}
            </h2>
            <p className="text-velvet_text-primary text-lg mb-2">
              {language === 'fr' 
                ? 'Vous avez atteint un nouveau niveau de complicité !'
                : 'You have reached a new level of intimacy!'
              }
            </p>
            <div className="flex items-center justify-center gap-2 mb-6">
              {[...Array(newLevel)].map((_, i) => (
                <Flame key={i} size={24} className="text-purple-light fill-current" />
              ))}
            </div>
            <p className="text-velvet_text-accent text-xl font-heading mb-6">
              {(LEVEL_NAMES[language] || LEVEL_NAMES.fr)[newLevel]}
            </p>
            <Button
              onClick={() => {
                setShowLevelUp(false);
                // Navigate to the new level's chat
                navigate(`/chat/${gender}/${characterId}/${newLevel}`);
              }}
              className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-8 py-3"
            >
              {language === 'fr' ? 'Passer au niveau suivant' : 'Go to next level'}
            </Button>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal - Shows every 15 messages or when paid level threshold reached */}
      {showUpgradePrompt && getNextLevelName() && (
        <div className="fixed inset-0 bg-black/65 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-velvet-paper rounded-2xl p-7 sm:p-8 max-w-md text-center animate-slideUp border border-terracotta-main/30 shadow-2xl">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-gradient-to-br from-terracotta-main to-terracotta-dark flex items-center justify-center shadow-lg">
              <Flame className="text-white" size={32} />
            </div>
            <h3
              className="text-2xl sm:text-3xl font-semibold text-velvet_text-primary leading-tight mb-3"
              style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            >
              {language === 'fr'
                ? `Niveau "${getNextLevelName()}" atteint !`
                : `"${getNextLevelName()}" level reached!`}
            </h3>
            <p className="text-velvet_text-primary/85 text-base mb-5 leading-relaxed">
              {language === 'fr'
                ? `Tu peux maintenant passer au niveau suivant avec ${characterName}.`
                : `You can now move up to the next level with ${characterName}.`}
            </p>
            <p className="text-velvet_text-primary text-lg font-medium mb-6">
              {language === 'fr' ? 'Débloquez pour ' : 'Unlock for '}
              {playPrices.oneTime.full && (
                <span className="line-through opacity-60 text-velvet_text-secondary mr-1">{playPrices.oneTime.full}</span>
              )}
              <span className="font-bold text-terracotta-main">{playPrices.oneTime.promo}</span>
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleQuickLevelPurchase}
                disabled={purchasingLevel}
                className="bg-terracotta-main hover:bg-terracotta-dark text-white rounded-full px-6 py-3 shadow-md shadow-terracotta-main/30"
                data-testid="upgrade-prompt-buy"
              >
                {purchasingLevel ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>
                    <Sparkles size={16} className="mr-2" />
                    {language === 'fr' ? 'Débloquer maintenant' : 'Unlock now'}
                  </>
                )}
              </Button>
            </div>
            {!isSubscribed && (
              <Button
                onClick={() => {
                  setShowUpgradePrompt(false);
                  setShowPremiumModal(true);
                }}
                variant="outline"
                className="mt-3 w-full border-purple-main/40 text-velvet_text-primary hover:bg-purple-main/10 hover:text-velvet_text-primary rounded-full px-6 py-2.5"
                data-testid="upgrade-prompt-premium"
              >
                <Sparkles size={14} className="mr-2 text-purple-light" />
                {({
                  fr: 'Expérience complète — 6,99 €/mois',
                  en: 'Full experience — €6.99/month',
                  es: 'Experiencia completa — 6,99 €/mes',
                  de: 'Volle Erfahrung — 6,99 €/Monat',
                  pt: 'Experiência completa — 6,99 €/mês',
                })[language] || 'Full experience — €6.99/month'}
              </Button>
            )}
            <button
              onClick={() => setShowUpgradePrompt(false)}
              className="text-velvet_text-secondary hover:text-velvet_text-primary text-sm mt-5 transition-colors"
              data-testid="upgrade-prompt-dismiss"
            >
              {({fr: 'Continuer la discussion', en: 'Continue chatting', es: 'Seguir conversando', de: 'Weiter chatten', pt: 'Continuar a conversa'})[language] || 'Continue chatting'}
            </button>
            <button
              onClick={() => navigate('/select-gender')}
              className="block mx-auto text-velvet_text-secondary/70 hover:text-velvet_text-primary text-xs mt-2 transition-colors"
              data-testid="upgrade-prompt-new-character"
            >
              {({fr: 'Choisir un autre personnage', en: 'Choose another character', es: 'Elegir otro personaje', de: 'Anderen Charakter wählen', pt: 'Escolher outro personagem'})[language] || 'Choose another character'}
            </button>
          </div>
        </div>
      )}

      {/* Progress Bar Header */}
      <div className="glass border-b border-white/5 px-4 pt-2 pb-5" style={{ overflow: 'visible' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <MessageSquare size={14} className="text-purple-light" />
              {nextLevelInfo.nextLevel ? (
                <span className="text-velvet_text-secondary text-xs">
                  <span className={`text-velvet_text-accent font-bold inline-block ${pulseDialogues ? 'animate-counter-pulse' : ''}`}>{localDialogues}/{nextLevelInfo.required}</span>
                  {(() => {
                    const levelName = (LEVEL_NAMES[language] || LEVEL_NAMES.fr)[nextLevelInfo.nextLevel];
                    const beforeText = { fr: 'avant', en: 'before', es: 'antes de', de: 'vor', pt: 'antes de' };
                    return ` ${beforeText[language] || beforeText.en} "${levelName}"`;
                  })()}
                </span>
              ) : (
                <span className="text-velvet_text-accent text-xs font-medium">
                  {language === 'fr' ? 'Tous les niveaux débloqués !' : language === 'es' ? '¡Todos los niveles desbloqueados!' : language === 'de' ? 'Alle Stufen freigeschaltet!' : language === 'pt' ? 'Todos os níveis desbloqueados!' : 'All levels unlocked!'}
                </span>
              )}
            </div>
          </div>
          <div className={`relative ${pulseDialogues ? 'animate-bar-glow' : ''}`} style={{ overflow: 'visible' }}>
            <Progress value={nextLevelInfo.progress} className="h-2.5 rounded-full" />
            <button
              onClick={() => navigate(`/monetization/${gender}/${characterId}`)}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-main to-fuchsia-main shadow-lg shadow-purple-main/40 hover:scale-110 text-white transition-all font-bold border-2 border-velvet-bg leading-none"
              style={{ fontSize: '18px', paddingBottom: '2px' }}
              data-testid="currency-level-button"
              title={language === 'fr' ? 'Débloquer des niveaux' : 'Unlock levels'}
            >
              {['fr', 'de', 'es', 'pt'].includes(language) ? '€' : '$'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Main Chat Area */}
        <main className="flex-1 flex flex-col min-h-0">
          {/* Chat Header */}
          <header className="glass border-b border-white/5 px-4 py-3 flex items-center gap-4">
            <button 
              onClick={() => navigate(`/select-level/${gender}/${characterId}`)}
              className="text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
              data-testid="back-to-select"
            >
              <ArrowLeft size={20} />
            </button>

            <div className="flex items-center gap-3 flex-1">
              {character?.image && (
                <SmartImage
                  src={character.image}
                  alt={characterName}
                  className="w-10 h-10 rounded-full shrink-0"
                />
              )}
              <div>
                <h2 className="text-velvet_text-primary font-medium flex items-center gap-2 flex-wrap">
                  {characterName}
                  {gender === 'couple' ? <Users size={16} className="text-purple-light" /> : <User size={16} className="text-purple-light" />}
                  {isSubscribed && <PremiumBadge expiry={subscriptionExpiry} language={language} />}
                </h2>
                <div className="flex items-center gap-1">
                  {[...Array(parseInt(level))].map((_, i) => (
                    <Flame key={i} size={12} className="text-purple-light fill-current" />
                  ))}
                  <span className="text-velvet_text-secondary text-xs ml-1">
                    {t(`level_${level}`)}
                  </span>
                </div>
              </div>
            </div>

            {/* Settings gear button */}
            <button
              onClick={() => setSettingsOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-velvet_text-secondary hover:text-velvet_text-accent transition-all"
              data-testid="settings-button"
            >
              <Settings size={20} />
            </button>
          </header>

          {/* Settings Sheet */}
          <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
            <SheetContent side="right" className="bg-velvet-bg border-white/10 w-[320px] sm:w-[380px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="text-velvet_text-primary font-heading text-xl">
                  {language === 'fr' ? 'Paramètres' : language === 'es' ? 'Ajustes' : language === 'de' ? 'Einstellungen' : language === 'pt' ? 'Definições' : 'Settings'}
                </SheetTitle>
              </SheetHeader>
              
              <div className="mt-6 space-y-5">
                {/* Subscription / Purchase */}
                <div className="glass rounded-xl p-4 space-y-3" data-testid="settings-purchase-section">
                  {!isSubscribed && (
                    <Button
                      onClick={() => {
                        setSettingsOpen(false);
                        setShowPremiumModal(true);
                      }}
                      className="w-full bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-full text-sm h-auto py-2 flex flex-col items-center"
                      data-testid="settings-subscribe-button"
                    >
                      <span className="font-semibold">{({fr: 'Abonnement Premium', en: 'Premium subscription', es: 'Suscripción Premium', de: 'Premium-Abonnement', pt: 'Assinatura Premium'})[language] || 'Premium subscription'}</span>
                      <span className="text-xs mt-0.5">
                        {playPrices.subscription.full && (
                          <span className="line-through opacity-70 mr-1">{playPrices.subscription.full}</span>
                        )}
                        {playPrices.subscription.promo}
                      </span>
                    </Button>
                  )}
                  
                  {getNextLevelName() && (
                    <Button
                      onClick={() => { setSettingsOpen(false); handleQuickLevelPurchase(); }}
                      variant="outline"
                      className="w-full border-white/20 text-velvet_text-primary hover:bg-white/10 rounded-full text-sm h-auto py-2 flex flex-col items-center"
                      data-testid="settings-next-level-button"
                    >
                      <span className="font-semibold">{language === 'fr' ? `Niveau "${getNextLevelName()}"` : `Level "${getNextLevelName()}"`}</span>
                      <span className="text-xs mt-0.5"><span className="line-through opacity-60 mr-1">{language === 'fr' ? '2,99 €' : '€2.99'}</span> {language === 'fr' ? '1,49 €' : '€1.49'}</span>
                    </Button>
                  )}
                  
                  {!photosUnlocked && (
                    <Button
                      onClick={() => { setSettingsOpen(false); handleUnlockPhotos(); }}
                      variant="outline"
                      className="w-full border-white/20 text-velvet_text-primary hover:bg-white/10 rounded-full text-sm h-auto py-2 flex flex-col items-center"
                      data-testid="settings-unlock-photos-button"
                    >
                      <span className="font-semibold flex items-center gap-1">
                        <ImageIcon size={14} />
                        {language === 'fr' ? `Toutes les photos de ${characterName}` : `All of ${characterName}'s photos`}
                      </span>
                      <span className="text-xs mt-0.5 text-velvet_text-secondary">
                        {language === 'fr' ? 'Accès illimité aux photos actuelles et futures' : 'Unlimited access to current and future photos'}
                      </span>
                      <span className="text-xs mt-0.5"><span className="line-through opacity-60 mr-1">{language === 'fr' ? '2,99 €' : '€2.99'}</span> {language === 'fr' ? '1,49 €' : '€1.49'}</span>
                    </Button>
                  )}
                </div>

                {/* Music toggle */}
                <div className="glass rounded-xl p-4 flex items-center justify-between" data-testid="settings-music-toggle">
                  <div className="flex items-center gap-3">
                    <Music size={18} className="text-purple-light" />
                    <span className="text-velvet_text-primary text-sm">
                      {{ fr: 'Musique', en: 'Music', es: 'Música', de: 'Musik', pt: 'Música' }[language] || 'Music'}
                    </span>
                  </div>
                  <Switch
                    checked={musicEnabled}
                    onCheckedChange={(checked) => {
                      setMusicEnabled(checked);
                      localStorage.setItem('velvet_music_muted', !checked ? 'true' : 'false');
                      window.dispatchEvent(new CustomEvent('musicToggle', { detail: { muted: !checked } }));
                    }}
                    className="data-[state=checked]:bg-purple-main data-[state=unchecked]:bg-white/30"
                    data-testid="music-switch"
                  />
                </div>

                {/* TTS toggle */}
                <div className="glass rounded-xl p-4 flex items-center justify-between" data-testid="settings-tts-toggle">
                  <div className="flex items-center gap-3">
                    {voiceEnabled ? <Volume2 size={18} className="text-purple-light" /> : <VolumeX size={18} className="text-purple-light" />}
                    <span className="text-velvet_text-primary text-sm">
                      {{ fr: 'Synthèse vocale', en: 'Text-to-speech', es: 'Síntesis de voz', de: 'Sprachsynthese', pt: 'Síntese de voz' }[language] || 'Text-to-speech'}
                    </span>
                  </div>
                  <Switch
                    checked={voiceEnabled}
                    onCheckedChange={(checked) => {
                      if (!checked && isPlayingVoice) stopVoice();
                      setVoiceEnabled(checked);
                    }}
                    className="data-[state=checked]:bg-purple-main data-[state=unchecked]:bg-white/30"
                    data-testid="tts-switch"
                  />
                </div>

                {/* Language */}
                <div className="glass rounded-xl p-4" data-testid="settings-language">
                  <div className="flex items-center gap-3 mb-3">
                    <Globe size={18} className="text-purple-light" />
                    <span className="text-velvet_text-primary text-sm">
                      {language === 'fr' ? 'Langue' : language === 'es' ? 'Idioma' : language === 'de' ? 'Sprache' : language === 'pt' ? 'Idioma' : 'Language'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { code: 'fr', label: 'FR' },
                      { code: 'en', label: 'EN' },
                      { code: 'es', label: 'ES' },
                      { code: 'de', label: 'DE' },
                      { code: 'pt', label: 'PT' }
                    ].map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => setLanguage(lang.code)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          language === lang.code 
                            ? 'bg-purple-main text-white' 
                            : 'bg-white/10 text-velvet_text-secondary hover:bg-white/20'
                        }`}
                        data-testid={`lang-${lang.code}-button`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Response length */}
                <div className="glass rounded-xl p-4" data-testid="settings-response-length">
                  <div className="flex items-center gap-3 mb-3">
                    <MessageCircle size={18} className="text-purple-light" />
                    <span className="text-velvet_text-primary text-sm">
                      {{ fr: 'Longueur des réponses', en: 'Response length', es: 'Longitud de respuesta', de: 'Antwortlänge', pt: 'Tamanho da resposta' }[language] || 'Response length'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { value: 'short', fr: 'Court', en: 'Short', es: 'Corto', de: 'Kurz', pt: 'Curto' },
                      { value: 'medium', fr: 'Moyen', en: 'Medium', es: 'Medio', de: 'Mittel', pt: 'Médio' },
                      { value: 'long', fr: 'Long', en: 'Long', es: 'Largo', de: 'Lang', pt: 'Longo' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setResponseLength(opt.value);
                          localStorage.setItem('velvet_response_length', opt.value);
                        }}
                        className={`flex-1 py-2 rounded-full text-xs font-medium transition-all ${
                          responseLength === opt.value 
                            ? 'bg-purple-main text-white' 
                            : 'bg-white/10 text-velvet_text-secondary hover:bg-white/20'
                        }`}
                        data-testid={`length-${opt.value}-button`}
                      >
                        {opt[language] || opt.en}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-white/10" />

                {/* FAQ */}
                <button
                  onClick={() => { setSettingsOpen(false); navigate('/faq'); }}
                  className="glass rounded-xl p-4 w-full flex items-center gap-3 text-velvet_text-primary hover:text-velvet_text-accent transition-colors"
                  data-testid="settings-faq-button"
                >
                  <HelpCircle size={18} className="text-purple-light" />
                  <span className="text-sm">{{ fr: 'Questions fréquentes (FAQ)', en: 'FAQ', es: 'Preguntas frecuentes (FAQ)', de: 'Häufige Fragen (FAQ)', pt: 'Perguntas frequentes (FAQ)' }[language] || 'FAQ'}</span>
                </button>

                {/* Contact */}
                <button
                  onClick={() => { setSettingsOpen(false); navigate('/contact'); }}
                  className="glass rounded-xl p-4 w-full flex items-center gap-3 text-velvet_text-primary hover:text-velvet_text-accent transition-colors"
                  data-testid="settings-contact-button"
                >
                  <Mail size={18} className="text-purple-light" />
                  <span className="text-sm">{{ fr: 'Nous contacter', en: 'Contact us', es: 'Contáctanos', de: 'Kontaktiere uns', pt: 'Contacte-nos' }[language] || 'Contact us'}</span>
                </button>

                {/* Report conversation */}
                <button
                  onClick={() => { setSettingsOpen(false); handleReport({ type: 'conversation', index: null, content: null, photoUrl: null }); }}
                  className="glass rounded-xl p-4 w-full flex items-center gap-3 text-velvet_text-primary hover:text-red-600 transition-colors"
                  data-testid="settings-report-button"
                >
                  <AlertTriangle size={18} className="text-red-500" />
                  <span className="text-sm">{{ fr: 'Signaler un contenu', en: 'Report content', es: 'Reportar contenido', de: 'Inhalt melden', pt: 'Denunciar conteúdo' }[language] || 'Report content'}</span>
                </button>

                {/* Promo / tester code */}
                <button
                  onClick={() => { setSettingsOpen(false); setPromoMessage(null); setPromoCode(''); setPromoOpen(true); }}
                  className="glass rounded-xl p-4 w-full flex items-center gap-3 text-velvet_text-primary hover:text-terracotta transition-colors"
                  data-testid="settings-promo-button"
                >
                  <Gift size={18} className="text-terracotta" />
                  <span className="text-sm">{{ fr: 'Saisir un code', en: 'Redeem a code', es: 'Canjear un código', de: 'Code einlösen', pt: 'Resgatar um código' }[language] || 'Redeem a code'}</span>
                </button>

                {/* Test push notifications */}
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/notifications/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      const data = res.data || {};
                      if (data.sent > 0) {
                        toast.success(data.message || (language === 'fr' ? 'Notification envoyée !' : 'Notification sent!'));
                      } else {
                        toast.error(data.message || (language === 'fr' ? "Active d'abord les notifications." : 'Enable notifications first.'));
                      }
                    } catch (err) {
                      toast.error(language === 'fr' ? `Erreur : ${err.message}` : `Error: ${err.message}`);
                    }
                  }}
                  className="glass rounded-xl p-4 w-full flex items-center gap-3 text-velvet_text-primary hover:text-terracotta transition-colors"
                  data-testid="settings-test-notif-button"
                >
                  <Bell size={18} className="text-terracotta" />
                  <span className="text-sm">{{ fr: 'Tester les notifications', en: 'Test notifications', es: 'Probar notificaciones', de: 'Benachrichtigungen testen', pt: 'Testar notificações' }[language] || 'Test notifications'}</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto min-h-0 p-4" data-testid="messages-area">
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-20">
                  {character?.image && (
                    <SmartImage
                      src={character.image}
                      alt={characterName}
                      className="w-24 h-24 rounded-full mx-auto mb-6"
                    />
                  )}
                  <h3 className="font-heading text-2xl text-velvet_text-primary mb-2">{characterName}</h3>
                  <p className="text-velvet_text-accent text-sm mb-2">{character?.personality}</p>
                  <p className="text-velvet_text-secondary">
                    {language === 'fr' 
                      ? `Commencez une conversation avec ${characterName}...`
                      : `Start a conversation with ${characterName}...`
                    }
                  </p>
                </div>
              )}

              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slideUp`}
                >
                  <div
                    className={`
                      max-w-[80%] p-4 rounded-2xl
                      ${msg.role === 'user' 
                        ? 'chat-bubble-user text-purple-100' 
                        : 'chat-bubble-ai text-zinc-200'
                      }
                    `}
                    data-testid={`message-${index}`}
                  >
                    {(msg.image_url || msg.video_url) && (() => {
                      const mediaUrl = msg.video_url || msg.image_url;
                      const isVideo = !!msg.video_url || /\.(mp4|webm|mov)(\?|$)/i.test(mediaUrl);
                      return (
                      <div className="mb-3 rounded-xl overflow-hidden relative">
                        {isVideo ? (
                          <video
                            src={mediaUrl}
                            controls
                            playsInline
                            preload="metadata"
                            className={`w-full max-w-[280px] h-auto rounded-xl ${msg.photo_locked ? 'blur-xl pointer-events-none' : ''}`}
                            data-testid={`video-${index}`}
                          />
                        ) : (
                        <img 
                          src={mediaUrl} 
                          alt="" 
                          className={`w-full max-w-[280px] h-auto rounded-xl ${msg.photo_locked ? 'blur-xl' : 'cursor-pointer active:scale-95 transition-transform'}`}
                          loading="lazy"
                          onClick={() => { if (!msg.photo_locked) setFullscreenPhoto(mediaUrl); }}
                          data-testid={`photo-${index}`}
                        />
                        )}
                        {msg.photo_locked && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 rounded-xl">
                            <Lock size={28} className="text-white mb-2" />
                            <p className="text-white text-xs font-medium mb-2 text-center px-4 leading-snug">
                              {(() => {
                                const name = characterName;
                                const TEMPLATES = {
                                  fr: `${name} se dévoile un peu plus à chaque photo 💫 Les prochaines sont ses préférées. Débloque sa galerie complète pour 1,49 €`,
                                  en: `${name} reveals a little more with every photo 💫 The next ones are her favorites. Unlock the full gallery for €1.49`,
                                  es: `${name} se muestra un poco más en cada foto 💫 Las siguientes son sus favoritas. Desbloquea su galería completa por 1,49 €`,
                                  de: `${name} zeigt sich mit jedem Foto etwas mehr 💫 Die nächsten sind ihre Liebsten. Schalte ihre komplette Galerie für 1,49 € frei`,
                                  pt: `${name} revela-se um pouco mais a cada foto 💫 As próximas são as suas favoritas. Desbloqueia a galeria completa por 1,49 €`,
                                };
                                return TEMPLATES[language] || TEMPLATES.en;
                              })()}
                            </p>
                            <button
                              onClick={handleUnlockPhotos}
                              disabled={purchasingPhotos}
                              className="bg-gradient-to-r from-purple-main to-fuchsia-main text-white text-xs font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
                              data-testid="unlock-photos-button"
                            >
                              {purchasingPhotos 
                                ? <Loader2 className="animate-spin" size={14} />
                                : (<>
                                    <ImageIcon size={12} className="inline mr-1" />
                                    {playPrices.oneTime.full && (
                                      <span className="line-through opacity-60 mr-1">{playPrices.oneTime.full}</span>
                                    )}
                                    {playPrices.oneTime.promo}
                                  </>)
                              }
                            </button>
                            {!isSubscribed && (
                              <button
                                onClick={() => setShowPremiumModal(true)}
                                className="mt-2 text-white/80 hover:text-white text-[11px] underline underline-offset-2 transition-colors"
                                data-testid="photo-overlay-premium-link"
                              >
                                {({
                                  fr: 'Ou débloque tout avec Premium — 6,99 €/mois',
                                  en: 'Or unlock everything with Premium — €6.99/month',
                                  es: 'O desbloquea todo con Premium — 6,99 €/mes',
                                  de: 'Oder alles mit Premium freischalten — 6,99 €/Monat',
                                  pt: 'Ou desbloqueia tudo com Premium — 6,99 €/mês',
                                })[language] || 'Or unlock everything with Premium — €6.99/month'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })()}
                    <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs opacity-50">
                        {new Date(msg.timestamp).toLocaleTimeString({ fr: 'fr-FR', en: 'en-US', es: 'es-ES', de: 'de-DE', pt: 'pt-PT' }[language] || 'en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                      {msg.role === 'assistant' && (
                        <button
                          onClick={() => handleReport({
                            type: (msg.image_url || msg.video_url) ? 'photo' : 'message',
                            index,
                            content: msg.content,
                            photoUrl: msg.image_url || msg.video_url || null
                          })}
                          className="text-velvet_text-secondary/60 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors p-1.5"
                          data-testid={`report-msg-${index}`}
                          title={language === 'fr' ? 'Signaler' : language === 'es' ? 'Reportar' : language === 'de' ? 'Melden' : language === 'pt' ? 'Denunciar' : 'Report'}
                          aria-label={language === 'fr' ? 'Signaler ce message' : 'Report this message'}
                        >
                          <Flag size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start animate-slideUp">
                  <div className="chat-bubble-ai px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-purple-light animate-bounce" style={{animationDelay: '0ms'}}></span>
                      <span className="w-2 h-2 rounded-full bg-purple-light animate-bounce" style={{animationDelay: '150ms'}}></span>
                      <span className="w-2 h-2 rounded-full bg-purple-light animate-bounce" style={{animationDelay: '300ms'}}></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="glass border-t border-white/5 p-4 shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <form onSubmit={sendMessage} className="max-w-3xl mx-auto flex gap-3">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isListening 
                  ? (language === 'fr' ? 'Parlez maintenant...' : 'Speak now...') 
                  : t('type_message')
                }
                className={`flex-1 bg-velvet-paper border-white/10 text-velvet_text-primary h-12 rounded-xl ${
                  isListening ? 'border-red-500/60' : ''
                }`}
                disabled={loading}
                data-testid="chat-input"
              />
              {/* Microphone button */}
              {speechSupported && (
                <div className="relative flex items-center">
                  {isListening && (
                    <div className="absolute right-full mr-3 flex items-center gap-2 whitespace-nowrap">
                      <span className="text-red-400 text-xs font-mono tabular-nums">
                        {Math.floor(listenDuration / 60).toString().padStart(2, '0')}:{(listenDuration % 60).toString().padStart(2, '0')}
                      </span>
                      <div className="flex items-center gap-[3px] h-6">
                        {[1,2,3,4,5].map(i => (
                          <div 
                            key={i}
                            className="w-[3px] bg-red-400 rounded-full"
                            style={{
                              animation: `voiceWave 0.8s ease-in-out ${i * 0.1}s infinite alternate`,
                              height: '8px',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <Button
                    type="button"
                    onClick={toggleListening}
                    className={`rounded-xl h-12 w-12 relative overflow-hidden ${
                      isListening 
                        ? 'bg-red-500 hover:bg-red-600' 
                        : 'bg-velvet-paper hover:bg-white/10 border border-white/10'
                    }`}
                    data-testid="mic-button"
                    title={language === 'fr' ? 'Dicter le message' : 'Dictate message'}
                  >
                    {isListening && (
                      <span className="absolute inset-0 rounded-xl border-2 border-red-400 animate-ping opacity-30" />
                    )}
                    {isListening ? (
                      <MicOff size={20} className="text-white relative z-10" />
                    ) : (
                      <Mic size={20} className="text-velvet_text-secondary" />
                    )}
                  </Button>
                </div>
              )}
              <Button
                type="submit"
                disabled={!input.trim() || loading}
                className="bg-purple-main hover:bg-purple-dark text-white rounded-xl h-12 w-12"
                data-testid="send-button"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Send size={20} />
                )}
              </Button>
            </form>
          </div>
        </main>
      </div>

      {/* Review Modal */}
      {showReview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="glass rounded-2xl p-6 max-w-sm w-full animate-slideUp border border-purple-main/30">
            
            {/* Step 1: Do you like the app? */}
            {reviewStep === 1 && (
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-main/30 to-fuchsia-main/30 flex items-center justify-center">
                  <Star className="text-purple-light" size={28} />
                </div>
                <h3 className="text-velvet_text-primary font-heading text-xl mb-2">
                  {language === 'fr' ? 'Aimez-vous MyCompanion AI ?' : language === 'es' ? '¿Te gusta MyCompanion AI?' : language === 'de' ? 'Gefällt Ihnen MyCompanion AI?' : language === 'pt' ? 'Gosta do MyCompanion AI?' : 'Do you like MyCompanion AI?'}
                </h3>
                <p className="text-velvet_text-secondary text-sm mb-6">
                  {language === 'fr' ? 'Votre avis nous aide à nous améliorer' : language === 'es' ? 'Tu opinión nos ayuda a mejorar' : language === 'de' ? 'Ihre Meinung hilft uns, besser zu werden' : language === 'pt' ? 'A sua opinião ajuda-nos a melhorar' : 'Your feedback helps us improve'}
                </p>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => { setReviewLiked(false); setReviewStep(2); }}
                    className="flex flex-col items-center gap-2 px-6 py-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
                    data-testid="review-dislike"
                  >
                    <ThumbsDown size={28} className="text-velvet_text-secondary" />
                    <span className="text-velvet_text-secondary text-sm">
                      {language === 'fr' ? 'Non' : language === 'es' ? 'No' : language === 'de' ? 'Nein' : language === 'pt' ? 'Não' : 'No'}
                    </span>
                  </button>
                  <button
                    onClick={() => { setReviewLiked(true); setReviewStep(2); }}
                    className="flex flex-col items-center gap-2 px-6 py-4 rounded-xl bg-purple-main/20 hover:bg-purple-main/30 transition-colors border border-purple-main/30"
                    data-testid="review-like"
                  >
                    <ThumbsUp size={28} className="text-purple-light" />
                    <span className="text-purple-light text-sm">
                      {language === 'fr' ? 'Oui' : language === 'es' ? 'Sí' : language === 'de' ? 'Ja' : language === 'pt' ? 'Sim' : 'Yes'}
                    </span>
                  </button>
                </div>
                <button
                  onClick={() => { setShowReview(false); setReviewDismissed(true); }}
                  className="mt-4 text-velvet_text-secondary text-xs hover:text-velvet_text-primary transition-colors"
                  data-testid="review-dismiss"
                >
                  {language === 'fr' ? 'Plus tard' : language === 'es' ? 'Más tarde' : language === 'de' ? 'Später' : language === 'pt' ? 'Mais tarde' : 'Later'}
                </button>
              </div>
            )}

            {/* Step 2a: User doesn't like - free comment */}
            {reviewStep === 2 && reviewLiked === false && (
              <div>
                <h3 className="text-velvet_text-primary font-heading text-lg mb-2">
                  {language === 'fr' ? 'Que pouvons-nous améliorer ?' : language === 'es' ? '¿Qué podemos mejorar?' : language === 'de' ? 'Was können wir verbessern?' : language === 'pt' ? 'O que podemos melhorar?' : 'What can we improve?'}
                </h3>
                <p className="text-velvet_text-secondary text-sm mb-4">
                  {language === 'fr' ? 'Dites-nous ce qui vous plairait davantage' : language === 'es' ? 'Cuéntanos qué te gustaría más' : language === 'de' ? 'Sagen Sie uns, was Ihnen besser gefallen würde' : language === 'pt' ? 'Diga-nos o que gostaria mais' : 'Tell us what you would enjoy more'}
                </p>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  className="w-full bg-velvet-paper border border-white/10 text-velvet_text-primary rounded-xl p-3 min-h-[120px] resize-none focus:outline-none focus:ring-2 focus:ring-purple-main/50 mb-4 text-sm"
                  placeholder={language === 'fr' ? 'Votre commentaire...' : language === 'es' ? 'Tu comentario...' : language === 'de' ? 'Ihr Kommentar...' : language === 'pt' ? 'O seu comentário...' : 'Your comment...'}
                  data-testid="review-comment-input"
                />
                <div className="flex gap-3">
                  <Button
                    onClick={() => { setReviewStep(1); setReviewComment(''); }}
                    variant="ghost"
                    className="flex-1 text-velvet_text-secondary rounded-full"
                  >
                    {t('back')}
                  </Button>
                  <Button
                    onClick={handleSubmitReview}
                    disabled={reviewSubmitting}
                    className="flex-1 bg-gradient-to-r from-purple-main to-fuchsia-main text-white rounded-full"
                    data-testid="review-submit"
                  >
                    {reviewSubmitting ? <Loader2 className="animate-spin" size={16} /> : (language === 'fr' ? 'Envoyer' : t('send'))}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2b: User likes - star rating + optional comment */}
            {reviewStep === 2 && reviewLiked === true && (
              <div>
                <h3 className="text-velvet_text-primary font-heading text-lg mb-2 text-center">
                  {language === 'fr' ? 'Super ! Notez-nous' : language === 'es' ? '¡Genial! Califícanos' : language === 'de' ? 'Toll! Bewerten Sie uns' : language === 'pt' ? 'Ótimo! Avalie-nos' : 'Great! Rate us'}
                </h3>
                <div className="flex justify-center gap-2 my-5">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      className="transition-transform hover:scale-110"
                      data-testid={`review-star-${star}`}
                    >
                      <Star
                        size={36}
                        className={`transition-colors ${
                          star <= reviewRating 
                            ? 'text-yellow-400 fill-yellow-400' 
                            : 'text-white/20'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  className="w-full bg-velvet-paper border border-white/10 text-velvet_text-primary rounded-xl p-3 min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-purple-main/50 mb-4 text-sm"
                  placeholder={language === 'fr' ? 'Un commentaire ? (optionnel)' : language === 'es' ? '¿Un comentario? (opcional)' : language === 'de' ? 'Ein Kommentar? (optional)' : language === 'pt' ? 'Um comentário? (opcional)' : 'A comment? (optional)'}
                  data-testid="review-comment-input"
                />
                <div className="flex gap-3">
                  <Button
                    onClick={() => { setReviewStep(1); setReviewRating(0); setReviewComment(''); }}
                    variant="ghost"
                    className="flex-1 text-velvet_text-secondary rounded-full"
                  >
                    {t('back')}
                  </Button>
                  <Button
                    onClick={handleSubmitReview}
                    disabled={reviewSubmitting || reviewRating === 0}
                    className="flex-1 bg-gradient-to-r from-purple-main to-fuchsia-main text-white rounded-full disabled:opacity-50"
                    data-testid="review-submit"
                  >
                    {reviewSubmitting ? <Loader2 className="animate-spin" size={16} /> : (language === 'fr' ? 'Envoyer' : t('send'))}
                  </Button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>

    {/* Report Dialog */}
    <AlertDialog open={reportOpen} onOpenChange={setReportOpen}>

    {/* Notification Permission Prompt */}
    {showNotifPrompt && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
        <div className="glass rounded-3xl p-6 max-w-sm w-full border border-white/10 shadow-2xl text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-main to-fuchsia-main flex items-center justify-center">
            <Bell size={28} className="text-white" />
          </div>
          <h3 className="text-velvet_text-primary font-heading text-lg mb-2">
            {language === 'fr' ? 'Ne manquez aucun message' : language === 'es' ? 'No te pierdas ningún mensaje' : language === 'de' ? 'Verpasse keine Nachricht' : language === 'pt' ? 'Não perca nenhuma mensagem' : "Don't miss any message"}
          </h3>
          <p className="text-velvet_text-secondary text-sm mb-5">
            {language === 'fr' ? `Activez les notifications pour recevoir les messages de vos personnages préférés, même quand vous n'êtes pas sur l'appli.`
            : language === 'es' ? 'Activa las notificaciones para recibir mensajes de tus personajes favoritos, incluso cuando no estés en la app.'
            : language === 'de' ? 'Aktiviere Benachrichtigungen, um Nachrichten von deinen Lieblingscharakteren zu erhalten, auch wenn du nicht in der App bist.'
            : language === 'pt' ? 'Ative as notificações para receber mensagens dos seus personagens favoritos, mesmo quando não estiver no app.'
            : 'Enable notifications to receive messages from your favorite characters, even when you\'re not on the app.'}
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-full border-white/20 text-velvet_text-secondary hover:text-white"
              onClick={() => { setShowNotifPrompt(false); }}
              data-testid="notif-dismiss"
            >
              {language === 'fr' ? 'Plus tard' : language === 'es' ? 'Más tarde' : language === 'de' ? 'Später' : language === 'pt' ? 'Mais tarde' : 'Later'}
            </Button>
            <Button
              className="flex-1 rounded-full bg-gradient-to-r from-purple-main to-fuchsia-main text-white hover:opacity-90"
              onClick={handleEnableNotifications}
              data-testid="notif-accept"
            >
              {language === 'fr' ? 'Activer' : language === 'es' ? 'Activar' : language === 'de' ? 'Aktivieren' : language === 'pt' ? 'Ativar' : 'Enable'}
            </Button>
          </div>
        </div>
      </div>
    )}
      <AlertDialogContent className="bg-white border-velvet_text-secondary/20 max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-velvet_text-primary flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-500" />
            {language === 'fr' ? 'Signaler un contenu' : language === 'es' ? 'Reportar contenido' : language === 'de' ? 'Inhalt melden' : language === 'pt' ? 'Denunciar conteúdo' : 'Report content'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-velvet_text-secondary">
            {reportDone 
              ? (language === 'fr' 
                ? 'Merci pour votre signalement. Notre équipe va examiner ce contenu.'
                : 'Thank you for your report. Our team will review this content.')
              : (language === 'fr'
                ? 'Sélectionnez la raison de votre signalement :'
                : 'Select the reason for your report:')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {reportDone ? (
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setReportOpen(false)}
              className="bg-terracotta-main hover:bg-terracotta-dark text-white rounded-full"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        ) : (
          <>
            <div className="space-y-2 my-2">
              {(REPORT_REASONS[language] || REPORT_REASONS.fr).map(r => (
                <button
                  key={r.value}
                  onClick={() => setReportReason(r.value)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm transition-all ${
                    reportReason === r.value
                      ? 'bg-red-50 border border-red-300 text-red-700'
                      : 'bg-velvet_text-secondary/5 border border-velvet_text-secondary/15 text-velvet_text-primary hover:bg-velvet_text-secondary/10'
                  }`}
                  data-testid={`report-reason-${r.value}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Description field - always shown, required for "other" */}
            <textarea
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              placeholder={language === 'fr' ? 'Décrivez le problème (optionnel)...' : 'Describe the issue (optional)...'}
              className="w-full bg-velvet_text-secondary/5 border border-velvet_text-secondary/15 rounded-xl p-3 text-sm text-velvet_text-primary placeholder:text-velvet_text-secondary/50 resize-none h-20 focus:outline-none focus:border-terracotta/50"
              data-testid="report-description"
            />
            {reportReason === 'other' && !reportDescription.trim() && (
              <p className="text-red-500 text-xs">{language === 'fr' ? 'Description obligatoire pour "Autre"' : 'Description required for "Other"'}</p>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel className="border-velvet_text-secondary/20 text-velvet_text-primary hover:bg-velvet_text-secondary/10 rounded-full">
                {language === 'fr' ? 'Annuler' : language === 'es' ? 'Cancelar' : language === 'de' ? 'Abbrechen' : language === 'pt' ? 'Cancelar' : 'Cancel'}
              </AlertDialogCancel>
              <button
                onClick={submitReport}
                disabled={!reportReason || reportSubmitting || (reportReason === 'other' && !reportDescription.trim())}
                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full px-6 py-2 text-sm font-medium transition-colors"
                data-testid="report-submit"
              >
                {reportSubmitting 
                  ? <Loader2 className="animate-spin" size={16} />
                  : (language === 'fr' ? 'Envoyer' : language === 'es' ? 'Enviar' : language === 'de' ? 'Senden' : language === 'pt' ? 'Enviar' : 'Submit')}
              </button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>

    {/* Promo Code Modal (testers) */}
    <AlertDialog open={promoOpen} onOpenChange={setPromoOpen}>
      <AlertDialogContent className="bg-white border-velvet_text-secondary/20 max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-velvet_text-primary flex items-center gap-2">
            <Gift size={20} className="text-terracotta" />
            {{ fr: 'Saisir un code', en: 'Redeem a code', es: 'Canjear un código', de: 'Code einlösen', pt: 'Resgatar um código' }[language] || 'Redeem a code'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-velvet_text-secondary">
            {{
              fr: "Si tu as reçu un code testeur ou un code promo, saisis-le ci-dessous pour débloquer les contenus.",
              en: "If you received a tester or promo code, enter it below to unlock content.",
              es: "Si has recibido un código de prueba o promocional, introdúcelo a continuación.",
              de: "Wenn du einen Tester- oder Promo-Code erhalten hast, gib ihn hier ein.",
              pt: "Se recebeste um código de testador ou promocional, insere-o abaixo."
            }[language] || "Enter your tester or promo code below to unlock content."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-2 space-y-3">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoMessage(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && promoCode.trim() && !promoSubmitting) handleRedeemPromo(); }}
            placeholder="XXXX-XXXXX-XXXX"
            className="w-full bg-velvet_text-secondary/5 border border-velvet_text-secondary/15 rounded-xl p-3 text-center text-base font-mono tracking-wider text-velvet_text-primary placeholder:text-velvet_text-secondary/40 uppercase focus:outline-none focus:border-terracotta/60"
            autoFocus
            data-testid="promo-code-input"
          />
          <button
            type="button"
            onClick={handleRedeemPromo}
            disabled={!promoCode.trim() || promoSubmitting || promoMessage?.type === 'success'}
            className="w-full bg-terracotta-main hover:bg-terracotta-dark disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-6 py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-sm"
            data-testid="promo-validate-inline"
          >
            {promoSubmitting
              ? <Loader2 className="animate-spin" size={16} />
              : ({ fr: 'Valider le code', en: 'Validate code', es: 'Validar el código', de: 'Code bestätigen', pt: 'Validar o código' }[language] || 'Validate code')}
          </button>
          {promoMessage && (
            <p
              className={`text-sm px-1 ${promoMessage.type === 'success' ? 'text-emerald-600' : 'text-red-500'}`}
              data-testid="promo-message"
            >
              {promoMessage.text}
            </p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel
            className="border-velvet_text-secondary/20 text-velvet_text-primary hover:bg-velvet_text-secondary/10 rounded-full"
            data-testid="promo-cancel"
          >
            {promoMessage?.type === 'success'
              ? ({ fr: 'Fermer', en: 'Close', es: 'Cerrar', de: 'Schließen', pt: 'Fechar' }[language] || 'Close')
              : ({ fr: 'Annuler', en: 'Cancel', es: 'Cancelar', de: 'Abbrechen', pt: 'Cancelar' }[language] || 'Cancel')}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Photo Fullscreen Lightbox */}
    {fullscreenPhoto && (
      <div 
        className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
        onClick={() => setFullscreenPhoto(null)}
        data-testid="photo-lightbox"
      >
        <button
          onClick={() => setFullscreenPhoto(null)}
          className="absolute top-4 right-4 text-white/70 hover:text-white bg-white/10 rounded-full p-2 backdrop-blur-sm transition-colors z-10"
          data-testid="lightbox-close"
        >
          <X size={24} />
        </button>
        <button
          onClick={async (e) => {
            e.stopPropagation();
            const filename = `mycompanion_${characterName}_${Date.now()}.jpg`;
            try {
              // Cross-origin <a download> is silently ignored — fetch as blob
              // so the browser sees a same-origin blob URL it WILL save.
              const res = await fetch(fullscreenPhoto, { mode: 'cors' });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const blob = await res.blob();

              // Mobile path: Web Share API with a File → triggers the system
              // share-sheet (Save image / Save to Photos / Send to ...).
              const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
              if (isMobile && navigator.canShare) {
                try {
                  const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
                  if (navigator.canShare({ files: [file] })) {
                    await navigator.share({ files: [file] });
                    return;
                  }
                } catch (_err) {
                  // User cancelled the share-sheet or share rejected → fall back
                  // to the desktop-style download below.
                }
              }

              const blobUrl = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = blobUrl;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
            } catch (err) {
              toast.error(language === 'fr' ? "Impossible d'enregistrer la photo." : 'Could not save the photo.');
              console.error('Photo download failed:', err);
            }
          }}
          className="absolute bottom-6 right-6 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-3 backdrop-blur-sm transition-colors z-10"
          data-testid="lightbox-download"
          aria-label={language === 'fr' ? 'Enregistrer la photo' : 'Save photo'}
        >
          <Download size={24} />
        </button>
        <img 
          src={fullscreenPhoto} 
          alt="" 
          className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}

    <PurchaseCelebrationModal
      open={!!celebration}
      title={(() => {
        const map = {
          level: { fr: 'Niveau débloqué !', en: 'Level unlocked!', es: '¡Nivel desbloqueado!', de: 'Stufe freigeschaltet!', pt: 'Nível desbloqueado!' },
          photos: { fr: 'Photos débloquées !', en: 'Photos unlocked!', es: '¡Fotos desbloqueadas!', de: 'Fotos freigeschaltet!', pt: 'Fotos desbloqueadas!' },
          subscription: { fr: 'Abonnement activé !', en: 'Subscription active!', es: '¡Suscripción activa!', de: 'Abo aktiv!', pt: 'Subscrição ativa!' },
        };
        return (map[celebration?.type] || map.level)[language] || 'Unlocked!';
      })()}
      message={(() => {
        const map = {
          level: {
            fr: 'L\'intensité de votre conversation peut maintenant monter d\'un cran. Profite bien',
            en: 'Your conversation can now reach a deeper intensity. Enjoy',
            es: 'La intensidad de tu conversación puede subir de nivel. Disfruta',
            de: 'Die Intensität deines Gesprächs kann jetzt eine Stufe höher gehen. Viel Spaß',
            pt: 'A intensidade da tua conversa pode agora subir um nível. Aproveita',
          },
          photos: {
            fr: 'Toutes les photos de ce personnage sont maintenant disponibles',
            en: 'All photos of this character are now available',
            es: 'Todas las fotos de este personaje ya están disponibles',
            de: 'Alle Fotos dieses Charakters sind jetzt verfügbar',
            pt: 'Todas as fotos desta personagem estão agora disponíveis',
          },
          subscription: {
            fr: "Tu as l'Abonnement Premium : tous les personnages, niveaux et photos débloqués + 200 messages/mois inclus par personnage. Profite à fond",
            en: 'You have the Premium subscription: all characters, levels and photos unlocked + 200 messages/month included per character. Enjoy',
            es: 'Tienes la Suscripción Premium: todos los personajes, niveles y fotos desbloqueados + 200 mensajes/mes incluidos por personaje. Disfruta',
            de: 'Du hast das Premium-Abonnement: alle Charaktere, Stufen und Fotos freigeschaltet + 200 Nachrichten/Monat pro Charakter inklusive. Viel Spaß',
            pt: 'Tens a Assinatura Premium: todas as personagens, níveis e fotos desbloqueadas + 200 mensagens/mês incluídas por personagem. Aproveita',
          },
        };
        return (map[celebration?.type] || map.level)[language] || '';
      })()}
      continueLabel={(language === 'fr' ? 'Continuer' : language === 'es' ? 'Continuar' : language === 'de' ? 'Weiter' : language === 'pt' ? 'Continuar' : 'Continue')}
      onContinue={() => {
        setCelebration(null);
        window.location.reload();
      }}
    />

    {/* Level 5 (Romance) message-block paywall.
        Triggered when the user hits the 81st / 161st / ... message at L5.
        Blocking modal — no escape via click outside (default AlertDialog behavior). */}
    <AlertDialog open={!!l5Paywall}>
      <AlertDialogContent
        className="bg-velvet_surface border border-terracotta-main/30 text-velvet_text-primary"
        data-testid="l5-paywall-modal"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-2xl text-terracotta-main">
            {l5Paywall?.isSubscriberQuota
              ? (({
                  fr: 'Quota mensuel atteint',
                  en: 'Monthly quota reached',
                  es: 'Cuota mensual alcanzada',
                  de: 'Monatliches Kontingent erreicht',
                  pt: 'Quota mensal atingida',
                })[language] || 'Monthly quota reached')
              : (({
                  fr: 'Continue ton aventure Romance',
                  en: 'Continue your Romance journey',
                  es: 'Continúa tu aventura Romance',
                  de: 'Setze deine Romance-Reise fort',
                  pt: 'Continua a tua jornada Romance',
                })[language] || 'Continue your Romance journey')}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-velvet_text-secondary pt-2">
            {l5Paywall?.isSubscriberQuota
              ? (({
                  fr: `Tu as utilisé tes ${l5Paywall?.monthlyQuota ?? 200} messages inclus ce mois-ci avec ${characterName}. Achète 80 messages supplémentaires, ou attends le 1er du mois prochain pour ta recharge automatique.`,
                  en: `You've used your ${l5Paywall?.monthlyQuota ?? 200} included messages this month with ${characterName}. Buy 80 extra messages, or wait until the 1st of next month for your automatic refill.`,
                  es: `Has usado tus ${l5Paywall?.monthlyQuota ?? 200} mensajes incluidos este mes con ${characterName}. Compra 80 mensajes extra, o espera al 1 del próximo mes para la recarga automática.`,
                  de: `Du hast deine ${l5Paywall?.monthlyQuota ?? 200} inkludierten Nachrichten diesen Monat mit ${characterName} aufgebraucht. Kaufe 80 weitere Nachrichten oder warte bis zum 1. nächsten Monats auf die automatische Aufladung.`,
                  pt: `Usaste as tuas ${l5Paywall?.monthlyQuota ?? 200} mensagens incluídas este mês com ${characterName}. Compra 80 mensagens extra, ou espera pelo dia 1 do próximo mês para a recarga automática.`,
                })[language] || '')
              : (({
                  fr: `Tu as échangé ${l5Paywall?.blockSize ?? 80} messages avec ${characterName} en Romance. Pour continuer, débloque les ${l5Paywall?.blockSize ?? 80} prochains messages.`,
                  en: `You've exchanged ${l5Paywall?.blockSize ?? 80} messages with ${characterName} in Romance. To keep going, unlock the next ${l5Paywall?.blockSize ?? 80} messages.`,
                  es: `Has intercambiado ${l5Paywall?.blockSize ?? 80} mensajes con ${characterName} en Romance. Para continuar, desbloquea los próximos ${l5Paywall?.blockSize ?? 80} mensajes.`,
                  de: `Du hast ${l5Paywall?.blockSize ?? 80} Nachrichten mit ${characterName} im Romance-Modus ausgetauscht. Schalte die nächsten ${l5Paywall?.blockSize ?? 80} frei, um weiterzumachen.`,
                  pt: `Trocaste ${l5Paywall?.blockSize ?? 80} mensagens com ${characterName} em Romance. Para continuar, desbloqueia as próximas ${l5Paywall?.blockSize ?? 80} mensagens.`,
                })[language] || '')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-col sm:flex-col gap-2 mt-4">
          <AlertDialogAction
            asChild
          >
            <Button
              onClick={async () => {
                if (purchasingL5Block) return;
                setPurchasingL5Block(true);
                try {
                  const res = await startPurchase({
                    itemType: 'l5_block',
                    itemId: characterId,
                    returnTo: `/chat/${gender}/${characterId}/5`,
                    onPopupClosed: () => {
                      setL5Paywall(null);
                      setActivating(true);
                    },
                  });
                  if (res.redirected) return;
                  if (res.success) {
                    setL5Paywall(null);
                    toast.success(
                      ({
                        fr: '80 messages débloqués ! Continue ta romance ✨',
                        en: '80 messages unlocked! Continue your romance ✨',
                        es: '¡80 mensajes desbloqueados! Continúa tu romance ✨',
                        de: '80 Nachrichten freigeschaltet! Setze deine Romanze fort ✨',
                        pt: '80 mensagens desbloqueadas! Continua a tua romance ✨',
                      })[language] || '80 messages unlocked!'
                    );
                  } else if (!res.cancelled) {
                    toast.error(res.message || (language === 'fr' ? "Erreur lors de l'achat" : 'Purchase error'));
                  }
                } catch (err) {
                  toast.error(err.message || (language === 'fr' ? "Erreur lors de l'achat" : 'Purchase error'));
                } finally {
                  setPurchasingL5Block(false);
                  setActivating(false);
                }
              }}
              disabled={purchasingL5Block}
              className="w-full bg-terracotta-main hover:bg-terracotta-dark text-white font-bold py-3 rounded-xl"
              data-testid="l5-paywall-buy-button"
            >
              {purchasingL5Block ? (
                ({fr: 'Traitement…', en: 'Processing…', es: 'Procesando…', de: 'Wird verarbeitet…', pt: 'A processar…'})[language] || 'Processing…'
              ) : (
                <>
                  {({
                    fr: `Débloquer 80 messages — `,
                    en: `Unlock 80 messages — `,
                    es: `Desbloquear 80 mensajes — `,
                    de: `80 Nachrichten freischalten — `,
                    pt: `Desbloquear 80 mensagens — `,
                  })[language] || 'Unlock 80 messages — '}
                  {playPrices.oneTime.full && (
                    <span className="line-through opacity-60 mr-1">{playPrices.oneTime.full}</span>
                  )}
                  <span className="font-bold">{playPrices.oneTime.promo}</span>
                </>
              )}
            </Button>
          </AlertDialogAction>
          {!isSubscribed && !l5Paywall?.isSubscriberQuota && (
            <Button
              onClick={() => {
                setL5Paywall(null);
                setShowPremiumModal(true);
              }}
              variant="outline"
              className="w-full border-purple-main/40 text-velvet_text-primary hover:bg-purple-main/10 hover:text-velvet_text-primary rounded-xl"
              data-testid="l5-paywall-premium-button"
            >
              <Sparkles size={14} className="mr-2 text-purple-light" />
              {({
                fr: 'Expérience complète — 6,99 €/mois',
                en: 'Full experience — €6.99/month',
                es: 'Experiencia completa — 6,99 €/mes',
                de: 'Volle Erfahrung — 6,99 €/Monat',
                pt: 'Experiência completa — 6,99 €/mês',
              })[language] || 'Full experience — €6.99/month'}
            </Button>
          )}
          <AlertDialogCancel
            asChild
          >
            <Button
              onClick={() => {
                setL5Paywall(null);
                navigate('/select-gender');
              }}
              variant="outline"
              className="w-full border-white/20 text-velvet_text-primary hover:bg-white/10 rounded-xl"
              data-testid="l5-paywall-home-button"
            >
              {({
                fr: "Retour à l'accueil",
                en: 'Back to home',
                es: 'Volver al inicio',
                de: 'Zur Startseite',
                pt: 'Voltar ao início',
              })[language] || 'Back to home'}
            </Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Premium subscription modal — opens from the settings button.
        onSubscribe routes to Google Play Billing via the existing
        `startPurchase({itemType:'subscription'})` flow. */}
    <PremiumModal
      isOpen={showPremiumModal}
      onClose={() => setShowPremiumModal(false)}
      lang={language}
      onSubscribe={async () => {
        try {
          const returnPath = `/chat/${gender}/${characterId}/${level}`;
          const res = await startPurchase({
            itemType: 'subscription',
            itemId: 'premium_monthly',
            returnTo: returnPath,
            onPopupClosed: () => {
              setShowPremiumModal(false);
              setActivating(true);
            },
          });
          if (res.redirected) return;
          setShowPremiumModal(false);
          if (res.success) {
            track('subscription_completed');
            setCelebration({ type: 'subscription' });
          } else if (!res.cancelled) {
            toast.error(res.message || 'Erreur');
          }
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
          setActivating(false);
        }
      }}
    />

    {/* Full-screen "Activation en cours…" loader, shown between successful
        Google Play popup close and the celebration modal appearing. */}
    <ActivationLoader isOpen={activating && !celebration} lang={language} />
    </>
  );
}
